import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, DollarSign, Mail, Camera, Upload, FileText, X, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(n || 0).toFixed(2)}`;
export const PAYMENT_METHODS = [
  { value: "cash_app", label: "Cash App" },
  { value: "zelle", label: "Zelle" },
  { value: "paypal", label: "PayPal" },
  { value: "stripe", label: "Stripe / Card" },
  { value: "check", label: "Check" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other" },
];
export const methodLabel = (v) => PAYMENT_METHODS.find((m) => m.value === v)?.label || v;
const today = () => new Date().toISOString().slice(0, 10);

export const CheckImageLink = ({ payment, size = "h-10 w-10" }) => {
  const [url, setUrl] = useState(null);
  const att = payment.attachment;
  useEffect(() => {
    if (!att) return undefined;
    let obj = null;
    axios.get(`${API}/payments/${payment.id}/check-image`, { responseType: "blob" }).then((r) => { obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => {});
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [payment.id, att]);
  if (!att) return null;
  const isPdf = att.content_type === "application/pdf";
  return (
    <a href={url || "#"} target="_blank" rel="noreferrer" onClick={(e) => !url && e.preventDefault()} title={att.filename || "Check image"} className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline" data-testid={`payment-check-image-${payment.id}`}>
      {isPdf || !url ? <FileText className="w-4 h-4" /> : <img src={url} alt="Check" className={`${size} rounded object-cover border border-slate-200`} />}
      {isPdf ? "View check (PDF)" : url ? "" : "Check image"}
    </a>
  );
};

const checkNumberFromNote = (note) => (note || "").match(/(?:check|chk|ck)\s*#?\s*(\d{2,})/i)?.[1] || "";

const AttachCheckDialog = ({ invoice, payment, onClose, onSaved }) => {
  const [checkNumber, setCheckNumber] = useState(payment.check_number || checkNumberFromNote(payment.note));
  const [picked, setPicked] = useState(null); // { file, previewUrl, attachment }
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fromPhoto, setFromPhoto] = useState(false);
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const pickFile = async (file) => {
    if (!file) return;
    if (picked?.previewUrl) URL.revokeObjectURL(picked.previewUrl);
    const next = { file, previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null, attachment: null };
    setPicked(next);
    setFromPhoto(false);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await axios.post(`${API}/invoices/${invoice.id}/payments/check-image?extract=1`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPicked({ ...next, attachment: up.attachment });
      const sg = up.suggestions || {};
      if (sg.check_number && !checkNumber.trim()) { setCheckNumber(sg.check_number); setFromPhoto(true); toast.info("Check # read from the photo — please confirm"); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload the check image");
      if (next.previewUrl) URL.revokeObjectURL(next.previewUrl);
      setPicked(null);
    } finally { setUploading(false); }
  };
  const save = async () => {
    if (!checkNumber.trim()) return toast.error("Check number is required");
    setSaving(true);
    try {
      await axios.patch(`${API}/payments/${payment.id}/check`, { check_number: checkNumber.trim(), attachment: picked?.attachment || null });
      toast.success("Check attached");
      if (picked?.previewUrl) URL.revokeObjectURL(picked.previewUrl);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to attach check");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="attach-check-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Paperclip className="w-4 h-4 text-slate-600" />{payment.attachment ? "Replace check" : "Attach check"} · {money(payment.amount)}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="attach-check-number">Check #</Label>
            <Input id="attach-check-number" value={checkNumber} onChange={(e) => { setCheckNumber(e.target.value); setFromPhoto(false); }} placeholder="1042" className={fromPhoto ? "border-amber-400 bg-amber-50" : ""} data-testid="attach-check-number-input" />
          </div>
          <div className="flex flex-wrap gap-2">
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="attach-check-camera-input" />
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="attach-check-file-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => cameraRef.current?.click()} data-testid="attach-check-take-photo-button"><Camera className="w-4 h-4 mr-1" /> Take photo</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="attach-check-upload-button"><Upload className="w-4 h-4 mr-1" /> Upload file</Button>
          </div>
          {picked && (
            <div className="flex items-center gap-3" data-testid="attach-check-preview">
              {picked.previewUrl ? <img src={picked.previewUrl} alt="Check preview" className="h-20 rounded border border-slate-200 object-contain bg-white" /> : <FileText className="w-8 h-8 text-slate-500" />}
              <span className="text-xs text-slate-600 truncate flex-1">{picked.file.name}{uploading ? " · reading…" : picked.attachment ? " · uploaded" : ""}</span>
              <button type="button" onClick={() => { if (picked.previewUrl) URL.revokeObjectURL(picked.previewUrl); setPicked(null); }} className="text-slate-400 hover:text-red-600" data-testid="attach-check-remove-file"><X className="w-4 h-4" /></button>
            </div>
          )}
          <p className="text-[11px] text-slate-500">Amount, date and balance are not changed. No receipt is sent.</p>
          <Button type="button" onClick={save} disabled={saving || uploading || !checkNumber.trim()} className="w-full" data-testid="attach-check-save-button">{uploading ? "Uploading…" : saving ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PaymentHistory = ({ payments, onDelete, onResend, onAttachCheck, busy }) => (
  <div className="rounded-lg border border-slate-200 divide-y" data-testid="payment-history">
    {payments.length === 0 ? <p className="p-3 text-sm text-slate-400">No payments recorded yet</p> : payments.map((p) => (
      <div key={p.id} className="flex items-center justify-between p-2.5 text-sm" data-testid={`payment-row-${p.id}`}>
        <div>
          <p className="font-medium text-slate-800">{money(p.amount)} <span className="text-slate-500 font-normal">· {methodLabel(p.method)}</span></p>
          <p className="text-xs text-slate-500">{p.check_number ? <span data-testid={`payment-check-number-${p.id}`}>Check #{p.check_number} · </span> : null}{p.date}{p.note ? ` · ${p.note}` : ""}{p.recorded_by ? ` · by ${p.recorded_by}` : ""}</p>
          {p.receipt_to && <p className="text-xs text-green-700" data-testid={`payment-receipt-status-${p.id}`}>Receipt emailed to {p.receipt_to}</p>}
        </div>
        <div className="flex items-center gap-1">
          <CheckImageLink payment={p} size="h-8 w-8" />
          {onAttachCheck && <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-600 hover:bg-slate-100" disabled={busy} onClick={() => onAttachCheck(p)} title={p.attachment ? "Replace check" : "Attach check"} data-testid={`payment-attach-check-${p.id}`}><Paperclip className="w-4 h-4" /></Button>}
          {onResend && <Button size="icon" variant="ghost" className="h-7 w-7 text-blue-600 hover:bg-blue-50" disabled={busy} onClick={() => onResend(p)} title={p.receipt_to ? "Resend receipt" : "Send receipt"} data-testid={`payment-receipt-${p.id}`}><Mail className="w-4 h-4" /></Button>}
          {onDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:bg-red-50" disabled={busy} onClick={() => onDelete(p)} title="Remove payment" data-testid={`payment-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>}
        </div>
      </div>
    ))}
  </div>
);

export const RecordPaymentDialog = ({ invoice, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ amount: "", method: "cash_app", date: today(), note: "", send_receipt: true, check_number: "" });
  const [checkFile, setCheckFile] = useState(null); // { file, previewUrl, attachment, suggestions }
  const [uploading, setUploading] = useState(false);
  const [suggested, setSuggested] = useState(null); // which fields were filled from the photo
  const [attachFor, setAttachFor] = useState(null); // payment being edited in AttachCheckDialog
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const pickFile = async (file) => {
    if (!file) return;
    if (checkFile?.previewUrl) URL.revokeObjectURL(checkFile.previewUrl);
    const next = { file, previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null, attachment: null, suggestions: null };
    setCheckFile(next);
    setSuggested(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await axios.post(`${API}/invoices/${invoice.id}/payments/check-image?extract=1`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCheckFile({ ...next, attachment: up.attachment, suggestions: up.suggestions });
      const sg = up.suggestions || {};
      const filled = {};
      setForm((f) => {
        const n = { ...f };
        if (sg.check_number && !f.check_number.trim()) { n.check_number = sg.check_number; filled.check_number = true; }
        if (sg.amount && sg.amount > 0 && sg.amount <= balance + 0.005) { n.amount = Number(sg.amount).toFixed(2); filled.amount = true; }
        if (sg.date) { n.date = sg.date; filled.date = true; }
        return n;
      });
      if (Object.keys(filled).length) { setSuggested(filled); toast.info("Filled from the check photo — please confirm before saving"); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload the check image");
      if (next.previewUrl) URL.revokeObjectURL(next.previewUrl);
      setCheckFile(null);
    } finally { setUploading(false); }
  };

  const load = async () => {
    const { data: d } = await axios.get(`${API}/invoices/${invoice.id}/payments`);
    setData(d);
    setForm((f) => ({ ...f, amount: d.balance_due > 0 ? d.balance_due.toFixed(2) : "" }));
  };
  useEffect(() => { load().catch(() => toast.error("Failed to load payments")); }, [invoice.id]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (form.method === "check" && !form.check_number.trim()) return toast.error("Check number is required");
    setBusy(true);
    try {
      const attachment = form.method === "check" && checkFile ? checkFile.attachment : null;
      const payload = { ...form, amount: parseFloat(form.amount), check_number: form.method === "check" ? form.check_number.trim() : null, attachment };
      const { data: res } = await axios.post(`${API}/invoices/${invoice.id}/payments`, payload);
      if (checkFile?.previewUrl) URL.revokeObjectURL(checkFile.previewUrl);
      setCheckFile(null);
      setSuggested(null);
      setForm((f) => ({ ...f, check_number: "" }));
      toast.success(res.status === "paid" ? `Invoice ${invoice.invoice_number} is now PAID` : `Payment recorded · balance ${money(res.balance_due)}`);
      if (res.receipt?.sent) toast.success(`Receipt emailed to ${res.receipt.to}`);
      else if (res.receipt?.error) toast.warning(`Payment saved, but the receipt email failed: ${res.receipt.error}`);
      await load();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payment");
    } finally {
      setBusy(false);
    }
  };

  const resend = async (p) => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/payments/${p.id}/receipt`);
      toast.success(`Receipt emailed to ${r.to}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send receipt");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/payments/${p.id}`);
      toast.success("Payment removed");
      await load();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove payment");
    } finally {
      setBusy(false);
    }
  };

  const balance = data?.balance_due ?? 0;
  const { can } = usePermissions();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-600" />Record Payment · {invoice.invoice_number}</DialogTitle>
        </DialogHeader>
        {data && (
          <div className="grid grid-cols-3 gap-2 text-center">
            {[["Total", data.total, "text-slate-800", "payment-total"], ["Paid", data.amount_paid, "text-green-600", "payment-paid"], ["Balance", balance, balance > 0 ? "text-red-600" : "text-green-600", "payment-balance"]].map(([l, v, c, id]) => (
              <div key={l} className="rounded-lg bg-slate-50 p-2"><p className="text-xs uppercase text-slate-500">{l}</p><p className={`font-bold ${c}`} data-testid={id}>{money(v)}</p></div>
            ))}
          </div>
        )}
        {balance > 0 && can("payments.record") && (
          <form onSubmit={submit} className="space-y-3" data-testid="record-payment-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pay-amount">Amount</Label>
                <Input id="pay-amount" type="number" step="0.01" min="0.01" max={balance.toFixed(2)} required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" />
              </div>
              <div>
                <Label htmlFor="pay-date">Date</Label>
                <Input id="pay-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-date-input" />
              </div>
            </div>
            <div>
              <Label>Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`payment-method-${m.value}`}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.method === "check" && (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3 bg-slate-50" data-testid="check-fields">
                <div>
                  <Label htmlFor="pay-check-number">Check #</Label>
                  <Input id="pay-check-number" required value={form.check_number} onChange={(e) => setForm({ ...form, check_number: e.target.value })} placeholder="1042" className={suggested?.check_number ? "border-amber-400 bg-amber-50" : ""} data-testid="payment-check-number-input" />
                  {suggested && <p className="text-[11px] text-amber-700 mt-1" data-testid="check-suggestions-note">Read from the photo: {[suggested.check_number && "check #", suggested.amount && "amount", suggested.date && "date"].filter(Boolean).join(", ")} — confirm or correct before saving.</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="check-camera-input" />
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="check-file-input" />
                  <Button type="button" variant="outline" size="sm" onClick={() => cameraRef.current?.click()} data-testid="check-take-photo-button"><Camera className="w-4 h-4 mr-1" /> Take photo</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="check-upload-button"><Upload className="w-4 h-4 mr-1" /> Upload file</Button>
                </div>
                {checkFile && (
                  <div className="flex items-center gap-3" data-testid="check-preview">
                    {checkFile.previewUrl ? <img src={checkFile.previewUrl} alt="Check preview" className="h-20 rounded border border-slate-200 object-contain bg-white" /> : <FileText className="w-8 h-8 text-slate-500" />}
                    <span className="text-xs text-slate-600 truncate flex-1">{checkFile.file.name}{uploading ? " · reading…" : checkFile.attachment ? " · uploaded" : ""}</span>
                    <button type="button" onClick={() => { if (checkFile.previewUrl) URL.revokeObjectURL(checkFile.previewUrl); setCheckFile(null); }} className="text-slate-400 hover:text-red-600" data-testid="check-remove-file"><X className="w-4 h-4" /></button>
                  </div>
                )}
                <p className="text-[11px] text-slate-500">Optional photo of the check (stored privately with your receipts).</p>
              </div>
            )}
            <div>
              <Label htmlFor="pay-note">Note (optional)</Label>
              <Input id="pay-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Check #1042, confirmation code…" data-testid="payment-note-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={form.send_receipt} onCheckedChange={(v) => setForm({ ...form, send_receipt: !!v })} data-testid="payment-send-receipt-checkbox" />
              Email a "Payment received" receipt to the customer (with updated invoice PDF)
            </label>
            <Button type="submit" disabled={busy || uploading || !form.amount} className="w-full bg-green-600 hover:bg-green-700" data-testid="payment-submit-button">
              {uploading ? "Uploading check…" : busy ? "Saving…" : parseFloat(form.amount || 0) >= balance - 0.005 ? "Record Payment & Mark Paid" : "Record Partial Payment"}
            </Button>
          </form>
        )}
        {data && balance <= 0 && <p className="text-sm text-green-700 bg-green-50 rounded-lg p-3 text-center" data-testid="payment-paid-in-full">This invoice is paid in full.</p>}
        <div>
          <p className="text-sm font-medium text-slate-700 mb-1">Payment history</p>
          <PaymentHistory payments={data?.payments || []} onDelete={can("payments.delete") ? remove : undefined} onResend={can("invoices.send") ? resend : undefined} onAttachCheck={can("payments.attach_check") ? setAttachFor : undefined} busy={busy} />
        </div>
        {attachFor && <AttachCheckDialog invoice={invoice} payment={attachFor} onClose={() => setAttachFor(null)} onSaved={async () => { setAttachFor(null); await load(); onChanged(); }} />}
      </DialogContent>
    </Dialog>
  );
};
